import React from 'react'
import { useLocation } from "react-router";
import { DashboardLayout } from "./Dashboard";
import { ColumnHeader } from "@/components/MyTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Bell, X, CheckCircle, MessageSquare, Clock } from "lucide-react";
import { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import TableWithSearch from "@/components/TableWithSearch";
import {
  formatDate,
  formatDateTime12,
  formatDateTime24,
  formatOrganizationTimezoneDisplay,
  getPatientContextTimezoneDisplay,
  getPromise,
  getPromiseToast,
} from "@/utils/helpers";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import { useNavigate } from "react-router";
import SnoozeClearTriageDialog from "@/components/SnoozeClearTriageDialog";
import { useAuth } from "@/contexts/use-auth";

/** Match Alerts list: clinic tz from org/facility/patient; `timezone` may be set by navigate state */
function getAlertHeaderTimezoneDisplay(alert: any): string {
  if (!alert || typeof alert !== "object") return "";
  const direct =
    alert.resolvedTimezone ??
    alert.timezone ??
    alert.timeZone;
  if (direct != null && String(direct).trim() !== "") {
    return formatOrganizationTimezoneDisplay(String(direct).trim());
  }
  if (alert.patient && typeof alert.patient === "object") {
    const tz = getPatientContextTimezoneDisplay(alert.patient);
    if (tz && tz !== "—") return tz;
  }
  const cr = alert.latestCriticalReading;
  if (cr && typeof cr === "object") {
    const patientType = cr.patientId?.typeOfPatient;
    if (patientType === "outpatient") {
      const iana =
        cr.organizationId?.timezone ?? cr.organizationId?.timeZone;
      return formatOrganizationTimezoneDisplay(iana);
    }
    if (patientType === "facility") {
      const iana = cr.facilityId?.timezone ?? cr.facilityId?.timeZone;
      return formatOrganizationTimezoneDisplay(iana);
    }
    const tz = getPatientContextTimezoneDisplay(cr.patientId);
    if (tz && tz !== "—") return tz;
  }
  const patientType = alert?.patientId?.typeOfPatient;
  if (patientType === "outpatient") {
    const iana =
      alert?.organizationId?.timezone ?? alert?.organizationId?.timeZone;
    return formatOrganizationTimezoneDisplay(iana);
  }
  if (patientType === "facility") {
    const iana = alert?.facilityId?.timezone ?? alert?.facilityId?.timeZone;
    return formatOrganizationTimezoneDisplay(iana);
  }
  return getPatientContextTimezoneDisplay(alert?.patientId);
}

/** Same text as Device / Reading / Received At columns (raw critical-reading row from API). */
function getCriticalReadingHistoryRowDisplay(alert: any) {
  const readingValues = alert?.readingValues || {};
  const readingValuesFormatted = Object.entries(readingValues)
    .map(([key, value]) => {
      const formattedKey =
        key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
      return `${formattedKey}: ${value}`;
    })
    .join(", ");
  const reading =
    readingValuesFormatted ||
    String(alert?.readingId?.readingId || alert?.readingId?._id || "");
  const device = alert?.deviceType
    ? alert.deviceType
        .replace(/_/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ")
    : "";
  const receivedAt = alert?.createdAtLocal
    ? alert.createdAtLocal ? formatDateTime12(alert.createdAt) : formatDateTime12(alert.createdAt)
    : alert?.readingId?.readingDate
      ? formatDateTime12(alert.readingId.readingDate)
      : "";
  return { reading, device, receivedAt };
}

const PedingAlertsAll = () => {
   const navigate = useNavigate();
   const location = useLocation();
   const { user } = useAuth();
   const isPatient = user?.role === "patient";
    const [refreshKey, setRefreshKey] = useState(0);
  const [receivedData, setReceivedData] = useState<any[]>([]);
  const [patientInfo, setPatientInfo] = useState<any>(null);

  const alert = location.state?.alert;

  // Get patient ID from the clicked alert (pending row: patientId object; resolved row: patient)
  const patientId =
    alert?.patient?._id ||
    (typeof alert?.patientId === "object" && alert?.patientId !== null
      ? alert.patientId._id
      : null) ||
    (typeof alert?.patientId === "string" ? alert.patientId : "") ||
    "";
   
  // Extract patient info from first alert when data is loaded
  useEffect(() => {
    if (receivedData.length > 0 && !patientInfo) {
      const firstAlert = receivedData[0];
      // console.log("firstAlert -> ",{firstAlert});
      if (firstAlert?.patientId) {
        setPatientInfo({
          patientId: firstAlert.patientId?.patientId || firstAlert.patientId?._id || "",
          name: `${firstAlert.patientId?.firstName || ""} ${firstAlert.patientId?.middleName || ""} ${firstAlert.patientId?.lastName || ""}`.trim(),
          phone: firstAlert.patientId?.primaryNumber || "",
          email: firstAlert.patientId?.email || "",
          organization: firstAlert?.organizationId?.organizationName || firstAlert?.organizationId || "",
          timezone: getAlertHeaderTimezoneDisplay(firstAlert),
        });
      }
    }
  }, [receivedData, patientInfo]);

  // Also set patient info from location.state.alert (pending: patientId; resolved list: patient)
  useEffect(() => {
    if (!alert || patientInfo) return;
    const patientRow = alert.patient || alert.patientId;
    // console.log("patientRow -> ",{patientRow});
    if (!patientRow || typeof patientRow !== "object") return;
    setPatientInfo({
      patientId:
        patientRow.patientId || patientRow._id || "",
      name: `${patientRow.firstName || ""} ${patientRow.middleName || ""} ${patientRow.lastName || ""}`.trim(),
      phone: patientRow.primaryNumber || "",
      email: patientRow.email || "",
      organization:
        (typeof alert.organizationId === "object" &&
          alert.organizationId?.organizationName) ||
        (typeof patientRow.organizationId === "object" &&
          patientRow.organizationId?.organizationName) ||
        alert.organizationId ||
        "",
      timezone: getAlertHeaderTimezoneDisplay(alert),
    });
  }, [alert, patientInfo]);

  // Column headers for Received tab (removed patient info columns)
  const receivedHeaders: ColumnHeader[] = [
    { id: "caretakerDevice", label: "Device", isSortable: false },
    { id: "reading", label: "Reading", isSortable: false },
    { id: "createdAt", label: "Reading Date", isSortable: true },
    { id: "resolvedAt", label: "Resolved At", isSortable: true },
    { id: "status", label: "Status", isSortable: false },
  ];
const handleRowClick = (data: any) => {
    // Handle row click if needed
    console.log("Row clicked:", data);
  };

  const handleResolve = (alert: any) => {
    const row = getCriticalReadingHistoryRowDisplay(alert);
    navigate(`/alerts/view/${alert?._id}`, {
      state: {
        alertHistoryRow: {
          reading: row.reading,
          device: row.device,
          timestamp: patientInfo.timezone || "N/A",
        },
      },
    });
  };


  return (
        <DashboardLayout activePage="/alerts">
      {patientInfo && (
        <Card className="mb-4">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">Patient ID</Label>
                <p className="text-sm font-medium mt-1">{patientInfo.patientId || "N/A"}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Name</Label>
                <p className="text-sm font-medium mt-1">{patientInfo.name || "N/A"}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Phone</Label>
                <p className="text-sm font-medium mt-1">{patientInfo.phone || "N/A"}</p>
              </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Timezone</Label>
                  <p className="text-sm font-medium mt-1">{patientInfo.timezone || "N/A"}</p>
                </div>
              <div>
                <Label className="text-sm text-muted-foreground">Organization</Label>
                <p className="text-sm font-medium mt-1">
                  {patientInfo.organization 
                    ? typeof patientInfo.organization === 'string'
                      ? patientInfo.organization
                          .split(' ')
                          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(' ')
                      : patientInfo.organization
                    : "N/A"}
                </p>
              </div>
              <div className="col-span-2">
                  <Label className="text-sm text-muted-foreground">Email</Label>
                  <p className="text-sm font-medium mt-1">{patientInfo.email || "N/A"}</p>
                </div>
            </div>
          </CardContent>
        </Card>
      )}
      <TableWithSearch
                  heading="Received Alerts"
                  url="/critical-readings"
                  universalLoadingSkeleton
                  // params={{ status: "resolved", patientId: "" || alert?.latestCriticalReading?._id  ,refreshKey }}
                  params={{ 
                    status: "resolved,invalid", 
                    ...(patientId ? { patientId: patientId } : {}),
                    refreshKey 
                  }}
                  apiDataKey="alerts"
                  fetchCondition={true}
                  isIncludeSearch={true}
                  searchPlaceholder="Search patient name"
                  columnHeaders={receivedHeaders}
                  data={receivedData}
                  viewData={receivedData.map((alert: any) => {
                    const row = getCriticalReadingHistoryRowDisplay(alert);
                    return {
                      ...alert,
                      _id: alert?.patientId?._id || alert?._id, // Preserve patient _id for navigation
                      caretakerDevice: row.device,
                      reading: row.reading,
                      createdAt: alert?.readingId?.readingDateLocal,
                      resolvedAt: alert?.resolvedAtLocal ? alert.resolvedAtLocal : "",
                      status: alert?.status?.replace(/_/g, " ") || "",
                    };
                  })}
                  setData={setReceivedData}
                  isActionColumn={false}
                  filtersOptions={[]}
                  handleAction={() => {}}
                  filters={{}}
                  setFilters={() => {}}
                  clickRow={handleRowClick}
                  isColumnSelection
                  columnPreferenceKey={`alerts-history-${patientId || "all"}`}
                  isDownloadData={user?.role?.toLowerCase() === "admin"}
                  isSelectable={false}
                  selectActions={[]}
                  donotFetch={false}
                  isIgnoreSearchParams={true}
                  moreActions={[
                    {
                      label: "View Data",
                      icon: <CheckCircle className="h-4 w-4" />,
                      onClick: handleResolve,
                      isShow: (alert: any) => alert?.status?.toLowerCase() !== "invalid",
                    },
                  ]}
                />

    </DashboardLayout>
  )
}

export default PedingAlertsAll